import {useState} from 'react';
import {useIntl} from 'react-intl';
import {Container} from 'react-bootstrap';
import {DateTime} from 'luxon';
import toast from 'react-hot-toast';
import {PageTitle} from '../../../_metronic/layout/core';
import Level3Title from '../../../app/common/components/title/Level3Title';
import FileInputControlled from '../../../app/modules/form/FileInputControlled';
import EligibiliteMasseGuide from '../Components/EligibiliteMasseGuide';
import {
  useDownloadEligibiliteMasseResult,
  useGetEligibiliteMasseSubmissions,
  useUploadEligibiliteMasseFile,
} from '../Hooks/DivopsHooks';

const DivopsMassEligibilitePage = (): JSX.Element => {
  const intl = useIntl();
  const [file, setFile] = useState<File | undefined>();
  const [inputResetKey, setInputResetKey] = useState(0);

  const {data: submissions = []} = useGetEligibiliteMasseSubmissions();
  const uploadMutation = useUploadEligibiliteMasseFile();
  const downloadMutation = useDownloadEligibiliteMasseResult();

  const handleFileChange = (e: React.ChangeEvent) => {
    const target = e.target as HTMLInputElement;
    setFile(target.files?.[0]);
  };

  const handleSubmit = () => {
    if (!file) {
      toast.error(intl.formatMessage({id: 'DIVOPS.MASSE.UPLOAD.ERROR.REQUIRED'}));
      return;
    }
    uploadMutation.mutate(file, {
      onSuccess: () => {
        toast.success(intl.formatMessage({id: 'DIVOPS.MASSE.UPLOAD.SUCCESS'}));
        setFile(undefined);
        setInputResetKey((key) => key + 1);
      },
      onError: (error: any) => {
        const message = error?.response?.data?.message;
        toast.error(message ?? intl.formatMessage({id: 'DIVOPS.MASSE.UPLOAD.ERROR.SERVER'}));
      },
    });
  };

  const handleDownload = (id: number) => {
    downloadMutation.mutate(id, {
      onError: () => {
        toast.error(intl.formatMessage({id: 'DIVOPS.MASSE.UPLOAD.ERROR.SERVER'}));
      },
    });
  };

  return (
    <Container fluid className="px-0">
      <PageTitle breadcrumbs={[]}>{intl.formatMessage({id: 'DIVOPS.MASSE.PAGE.TITLE'})}</PageTitle>

      <EligibiliteMasseGuide />

      <div className="card mb-8">
        <div className="card-header border-0 pt-6">
          <div className="card-title">
            <Level3Title title={intl.formatMessage({id: 'DIVOPS.MASSE.UPLOAD.TITLE'})} />
          </div>
        </div>
        <div className="card-body">
          <div className="d-flex flex-column flex-sm-row align-items-stretch align-items-sm-end gap-3">
            <div className="flex-grow-1">
              <FileInputControlled
                key={inputResetKey}
                name="eligibiliteMasseFile"
                label={intl.formatMessage({id: 'DIVOPS.MASSE.UPLOAD.LABEL'})}
                accept=".csv"
                value={file}
                onChange={handleFileChange}
                onReset={undefined}
                touched={undefined}
                errors={undefined}
              />
            </div>
            <button
              type="button"
              className="btn btn-lg btn-primary flex-shrink-0"
              onClick={handleSubmit}
              disabled={uploadMutation.isLoading}
            >
              {uploadMutation.isLoading ? (
                <span className="indicator-progress d-flex align-items-center">
                  {intl.formatMessage({id: 'WAIT'})}
                  <span className="spinner-border spinner-border-sm align-middle ms-2"></span>
                </span>
              ) : (
                <>
                  <i className="fas fa-upload me-2"></i>
                  {intl.formatMessage({id: 'DIVOPS.MASSE.UPLOAD.SUBMIT'})}
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      <div className="card">
        <div className="card-header border-0 pt-6">
          <div className="card-title">
            <Level3Title title={intl.formatMessage({id: 'DIVOPS.MASSE.LIST.TITLE'})} />
          </div>
        </div>
        <div className="card-body">
          {submissions.length > 0 ? (
            <div className="table-responsive">
              <table className="table table-row-bordered table-row-gray-100 align-middle gs-0 gy-4">
                <thead>
                  <tr className="fw-bold text-muted bg-light">
                    <th className="ps-4 min-w-200px rounded-start">
                      {intl.formatMessage({id: 'DIVOPS.MASSE.LIST.COL.FILE'})}
                    </th>
                    <th className="min-w-100px">
                      {intl.formatMessage({id: 'DIVOPS.MASSE.LIST.COL.COUNT'})}
                    </th>
                    <th className="min-w-150px">
                      {intl.formatMessage({id: 'DIVOPS.MASSE.LIST.COL.DATE'})}
                    </th>
                    <th className="min-w-100px">
                      {intl.formatMessage({id: 'DIVOPS.MASSE.LIST.COL.STATUS'})}
                    </th>
                    <th className="min-w-100px text-end rounded-end pe-4"></th>
                  </tr>
                </thead>
                <tbody>
                  {submissions.map((submission) => (
                    <tr key={submission.id}>
                      <td className="ps-4 text-dark fw-bold fs-6">{submission.fileName}</td>
                      <td className="text-dark fs-6">{submission.addressCount}</td>
                      <td className="text-dark fs-6">
                        {DateTime.fromISO(submission.submittedAt).toFormat('dd/MM/yyyy HH:mm')}
                      </td>
                      <td>
                        {submission.statut === 'termine' && (
                          <span className="badge badge-light-success fw-semibold">
                            {intl.formatMessage({id: 'DIVOPS.MASSE.LIST.STATUS.DONE'})}
                          </span>
                        )}
                        {submission.statut === 'en_attente' && (
                          <span className="badge badge-light-warning fw-semibold">
                            {intl.formatMessage({id: 'DIVOPS.MASSE.LIST.STATUS.PENDING'})}
                          </span>
                        )}
                        {submission.statut === 'erreur' && (
                          <span className="badge badge-light-danger fw-semibold">
                            {intl.formatMessage({id: 'DIVOPS.MASSE.LIST.STATUS.ERROR'})}
                          </span>
                        )}
                      </td>
                      <td className="text-end pe-4">
                        <button
                          type="button"
                          className="btn btn-sm btn-light-primary"
                          disabled={submission.statut !== 'termine'}
                          onClick={() => handleDownload(submission.id)}
                        >
                          <i className="fas fa-download me-2"></i>
                          {intl.formatMessage({id: 'DIVOPS.MASSE.LIST.DOWNLOAD'})}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-muted">{intl.formatMessage({id: 'DIVOPS.MASSE.LIST.EMPTY'})}</div>
          )}
        </div>
      </div>
    </Container>
  );
};

export default DivopsMassEligibilitePage;
